import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
} from "react";

interface AuthContextType {
  token: string | null;
  role: string | null;
  isAuthenticated: boolean;
  login: (newToken: string, newRole: string) => void;
  switchRole: (newRole: string) => void;
  logout: () => void;
  setToken: (token: string | null) => void;
  setRole: (role: string | null) => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Helper to safely get from localStorage (handles SSR and errors)
const getStoredValue = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

// Helper to safely set localStorage
const setStoredValue = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    console.error(`Failed to store ${key} in localStorage`);
  }
};

// Helper to clear localStorage
const clearStorage = (): void => {
  try {
    localStorage.clear();
  } catch {
    console.error("Failed to clear localStorage");
  }
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: AuthProviderProps) => {
  // Initialize state directly from localStorage to persist across refreshes
  const [token, setToken] = useState<string | null>(() => getStoredValue("token"));
  const [role, setRole] = useState<string | null>(() => getStoredValue("role"));

  const login = useCallback((newToken: string, newRole: string) => {
    setToken(newToken);
    setRole(newRole);
    setStoredValue("token", newToken);
    setStoredValue("role", newRole);
  }, []);

  const switchRole = useCallback((newRole: string) => {
    setRole(newRole);
    setStoredValue("role", newRole);
  }, []);

  const logout = useCallback(() => {
    setRole(null);
    setToken(null);
    clearStorage();
  }, []);

  const isAuthenticated = Boolean(token);

  const contextValue = useMemo(
    () => ({
      token,
      role,
      isAuthenticated,
      login,
      switchRole,
      logout,
      setToken,
      setRole,
    }),
    [token, role, isAuthenticated, login, switchRole, logout]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
